import React, { useEffect, useRef, useState } from 'react';

const SCRIPT_URL = 'https://platform.aminos.ai/w/chat_form_plugin.js';
const TOUR_URL = 'https://tour.paduaweddings.com';
const ESTATE_IMAGE_URL = 'https://ik.imagekit.io/aeu02xvjgn/09-padua-hills-claremont-wedding-photography.jpg';
const VIDEO_PLAYLIST = [
  'https://ik.imagekit.io/aeu02xvjgn/Summer%20Promotion/0520(5).mov/ik-video.mp4?updatedAt=1781381286449',
  'https://ik.imagekit.io/aeu02xvjgn/Summer%20Promotion/0520.mov/ik-video.mp4?updatedAt=1781381216567',
  'https://ik.imagekit.io/aeu02xvjgn/Summer%20Promotion/0606%20(1).mov/ik-video.mp4?updatedAt=1781380963578'
];

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

export default function AminosInlineChat() {
  const mountRef = useRef(null);
  const videoRef = useRef(null);
  const failedVideoIndices = useRef(new Set());
  const [retryCount, setRetryCount] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [videoIndex, setVideoIndex] = useState(0);
  const [videoUnavailable, setVideoUnavailable] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
  const [isPaused, setIsPaused] = useState(prefersReducedMotion);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let active = true;
    const script = document.createElement('script');
    script.async = true;
    script.src = SCRIPT_URL;
    script.dataset.botId = '12862';

    const handleError = () => {
      if (active) setLoadError(true);
    };

    setLoadError(false);
    script.addEventListener('error', handleError);
    document.body.appendChild(script);

    return () => {
      active = false;
      script.removeEventListener('error', handleError);
      script.remove();
      mount.replaceChildren();
    };
  }, [retryCount]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handlePreferenceChange = (event) => {
      setReducedMotion(event.matches);
      if (event.matches) {
        videoRef.current?.pause();
        setIsPaused(true);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handlePreferenceChange);
      return () => mediaQuery.removeEventListener('change', handlePreferenceChange);
    }

    mediaQuery.addListener(handlePreferenceChange);
    return () => mediaQuery.removeListener(handlePreferenceChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || videoUnavailable) return undefined;

    let active = true;
    video.load();

    if (!isPaused) {
      const playRequest = video.play();
      playRequest?.catch(() => {
        if (active) setIsPaused(true);
      });
    }

    return () => {
      active = false;
    };
  }, [videoIndex, isPaused, videoUnavailable]);

  const advanceVideo = (fromIndex, failed = false) => {
    if (failed) failedVideoIndices.current.add(fromIndex);

    for (let offset = 1; offset <= VIDEO_PLAYLIST.length; offset += 1) {
      const nextIndex = (fromIndex + offset) % VIDEO_PLAYLIST.length;
      if (failedVideoIndices.current.has(nextIndex)) continue;

      if (nextIndex === fromIndex) {
        const video = videoRef.current;
        if (video) {
          video.currentTime = 0;
          if (!isPaused) {
            video.play().catch(() => setIsPaused(true));
          }
        }
        return;
      }

      setVideoIndex(nextIndex);
      return;
    }

    setVideoUnavailable(true);
    setIsPaused(true);
  };

  return (
    <section
      className="bg-brand-ivory px-5 py-10 sm:px-6 sm:py-12"
      style={{ backgroundColor: '#f7f5ef' }}
      aria-labelledby="padua-chat-heading"
    >
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-7 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:items-stretch lg:gap-8">
          <div className="min-w-0">
            <div className="relative mb-6 aspect-video w-full overflow-hidden bg-[#171815]">
              {videoUnavailable ? (
                <img
                  src={ESTATE_IMAGE_URL}
                  alt="Wedding photography at Padua Hills Theatre in Claremont"
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-contain"
                />
              ) : (
                <video
                  ref={videoRef}
                  src={VIDEO_PLAYLIST[videoIndex]}
                  poster={ESTATE_IMAGE_URL}
                  autoPlay={!isPaused && !reducedMotion}
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden="true"
                  onEnded={() => advanceVideo(videoIndex)}
                  onError={() => advanceVideo(videoIndex, true)}
                  className="h-full w-full object-contain"
                />
              )}
              <div
                className="absolute inset-0 bg-gradient-to-t from-[#3f463b]/45 via-transparent to-[#3f463b]/5"
                aria-hidden="true"
              />
            </div>

            <div className="mb-6 flex flex-col gap-4">
              <div className="max-w-2xl">
                <p className="mb-3 font-sans text-[10px] font-semibold uppercase tracking-[0.3em] text-brand-olive">
                  Your Padua Concierge
                </p>
                <h2 id="padua-chat-heading" className="font-serif text-3xl font-light tracking-tight text-brand-charcoal sm:text-4xl">
                  Your celebration starts with a conversation.
                </h2>
                <p className="mt-3 font-sans text-sm font-light leading-relaxed text-brand-charcoal/80 sm:text-base">
                  Ask about the estate, menus, wedding pricing, or planning your private tour.
                </p>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-2 font-sans text-xs">
                <a
                  href={TOUR_URL}
                  className="font-semibold uppercase tracking-[0.12em] text-brand-olive underline underline-offset-4 transition-colors hover:text-brand-charcoal"
                >
                  Book a Private Tour
                </a>
                <a href="/privacy-policy" className="text-brand-charcoal/70 underline underline-offset-4 hover:text-brand-charcoal">
                  Privacy Policy
                </a>
              </div>
            </div>

            <p className="mt-3 font-sans text-xs leading-relaxed text-brand-charcoal/60">
              You’re chatting with Padua’s AI concierge.
            </p>
          </div>

          <div className="w-full min-w-0 overflow-visible rounded-sm border border-brand-stone/70 bg-brand-vintage p-3 sm:p-5">
            <div
              id="chat_form"
              ref={mountRef}
              className="aminos-chat-host w-full min-w-0 overflow-visible"
              style={{ minHeight: 'min(680px, 560px)' }}
            />
            {loadError && (
              <div className="mt-4 flex flex-wrap items-center gap-4 border-t border-brand-stone/50 pt-4" role="alert">
                <p className="font-sans text-sm text-brand-charcoal/80">
                  The chat could not load. Please try again or arrange a private tour.
                </p>
                <button
                  type="button"
                  onClick={() => setRetryCount((count) => count + 1)}
                  className="min-h-11 border border-brand-olive px-4 py-2 font-sans text-[10px] font-bold uppercase tracking-[0.14em] text-brand-olive transition-colors hover:bg-brand-olive hover:text-brand-vintage"
                >
                  Retry chat
                </button>
                <a
                  href={TOUR_URL}
                  className="font-sans text-xs font-semibold text-brand-olive underline underline-offset-4 hover:text-brand-charcoal"
                >
                  Book a Private Tour
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .aminos-chat-host {
          height: 560px;
        }
        @media (min-width: 768px) {
          .aminos-chat-host {
            height: 680px;
          }
        }
      `}</style>
    </section>
  );
}